import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, Store, Truck, Check, Loader2, ShoppingBag, LogIn } from "lucide-react";
import { api, money, formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import PaymentMethodModal from "@/components/PaymentMethodModal";
import { toast } from "sonner";

const MIN_ORDER = 40;
const DELIVERY_FEE = 5;

// Prochains lundis pour la livraison
function nextMondays(count = 6) {
  const out = [];
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  while (out.length < count) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() === 1) out.push(new Date(d));
  }
  return out;
}
function nextWeekdays(count = 8) {
  const out = [];
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  while (out.length < count) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() >= 1 && d.getDay() <= 5) out.push(new Date(d));
  }
  return out;
}
const fmtDate = (d) => d.toLocaleDateString("fr-CA", { weekday: "long", day: "numeric", month: "long" });
const iso = (d) => d.toISOString().slice(0, 10);

export default function Checkout() {
  const nav = useNavigate();
  const { items, subtotal, clear } = useCart();
  const { user, openAuth } = useAuth();

  const [mode, setMode] = useState("cueillette");
  const [busy, setBusy] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [form, setForm] = useState({
    contact_name: "", contact_email: "", contact_phone: "",
    address: "", postal_code: "", city: "", date: "", time: "", notes: "",
  });

  useEffect(() => {
    if (user) setForm((f) => ({
      ...f,
      contact_name: f.contact_name || user.name || "",
      contact_email: f.contact_email || user.email || "",
      contact_phone: f.contact_phone || user.phone || "",
      address: f.address || user.address || "",
      postal_code: f.postal_code || user.postal_code || "",
      city: f.city || user.city || "",
    }));
  }, [user]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const dates = mode === "livraison" ? nextMondays() : nextWeekdays();
  const deliveryFee = mode === "livraison" ? DELIVERY_FEE : 0;
  const total = subtotal + deliveryFee;

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <ShoppingBag className="h-14 w-14 text-muted-foreground mx-auto mb-4" />
        <h1 className="font-heading text-2xl font-bold text-foreground">Votre panier est vide</h1>
        <button onClick={() => nav("/menu")} className="mt-6 px-6 py-3 rounded-full bg-primary text-primary-foreground font-heading font-semibold">Parcourir le menu</button>
      </div>
    );
  }

  const submit = (e) => {
    e.preventDefault();
    if (subtotal < MIN_ORDER) { toast.error(`Minimum de commande : ${money(MIN_ORDER)}`); return; }
    if (!form.date) { toast.error("Veuillez choisir une date"); return; }
    if (mode === "livraison" && (!form.address || !form.postal_code)) { toast.error("Adresse de livraison requise"); return; }
    if (!user) {
      toast.info("Créez un compte ou connectez-vous pour finaliser et payer votre commande.");
      openAuth();
      return;
    }
    setPayOpen(true);
  };

  const placeOrder = async () => {
    setBusy(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id, nom: i.nom, prix: i.prix, quantity: i.quantity,
          image: i.image, guests: i.guests || null, servings: i.servings || 1, note: i.note || "", options: i.options || [],
        })),
        ...form, mode,
      };
      const { data } = await api.post("/orders", payload);
      clear();
      sessionStorage.setItem("tfl_last_order", JSON.stringify(data));
      setPayOpen(false);
      nav("/confirmation");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setPayOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-foreground mb-8">Finaliser la commande</h1>

      <form onSubmit={submit} className="grid lg:grid-cols-[1fr_380px] gap-8">
        <div className="space-y-6">
          {/* Contact */}
          <Section n="1" title="Vos coordonnées">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Nom complet" required value={form.contact_name} onChange={set("contact_name")} testid="checkout-name" />
              <Field label="Téléphone" required value={form.contact_phone} onChange={set("contact_phone")} testid="checkout-phone" />
              <div className="sm:col-span-2">
                <Field label="Courriel" type="email" required value={form.contact_email} onChange={set("contact_email")} testid="checkout-email" />
              </div>
            </div>
          </Section>

          {/* Fulfillment */}
          <Section n="2" title="Mode de service">
            <div className="grid grid-cols-2 gap-3">
              <ModeBtn active={mode === "cueillette"} onClick={() => { setMode("cueillette"); setForm((f) => ({ ...f, date: "" })); }} icon={Store} label="À emporter" sub="374 Saint Joseph" testid="checkout-mode-cueillette" />
              <ModeBtn active={mode === "livraison"} onClick={() => { setMode("livraison"); setForm((f) => ({ ...f, date: "" })); }} icon={Truck} label="Livraison" sub="Lundi · 5 $" testid="checkout-mode-livraison" />
            </div>

            {mode === "livraison" && (
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2"><Field label="Adresse" required value={form.address} onChange={set("address")} testid="checkout-address" /></div>
                <Field label="Code postal" required value={form.postal_code} onChange={set("postal_code")} testid="checkout-postal" />
                <Field label="Ville" value={form.city} onChange={set("city")} testid="checkout-city" />
                <p className="sm:col-span-2 text-xs text-coral bg-muted border border-border rounded-lg px-3 py-2">📍 Livraison le lundi seulement dans la région de Portneuf.</p>
              </div>
            )}

            <div className="mt-4">
              <label className="font-heading text-sm font-semibold text-foreground">Date de {mode === "livraison" ? "livraison" : "cueillette"}</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {dates.map((d) => (
                  <button type="button" key={iso(d)} onClick={() => setForm((f) => ({ ...f, date: iso(d) }))}
                    data-testid={`checkout-date-${iso(d)}`}
                    className={`px-3 py-2 rounded-lg text-sm border transition-colors ${form.date === iso(d) ? "bg-primary border-coral text-primary-foreground" : "bg-foreground/5 border-border text-muted-foreground hover:border-coral/40"}`}>
                    {fmtDate(d)}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-4 max-w-[200px]">
              <Field label="Heure souhaitée" type="time" value={form.time} onChange={set("time")} testid="checkout-time" />
            </div>
          </Section>

          {/* Notes */}
          <Section n="3" title="Note (optionnel)">
            <textarea value={form.notes} onChange={set("notes")} maxLength={200} rows={3} data-testid="checkout-notes"
              placeholder="Instructions spéciales, allergies..."
              className="w-full px-4 py-3 rounded-lg bg-foreground/5 border border-border text-foreground placeholder:text-muted-foreground focus:border-coral focus:outline-none resize-none text-sm" />
          </Section>

          {/* Payment */}
          <Section n="4" title="Paiement">
            <div className="flex items-center gap-3 p-4 rounded-xl bg-muted border border-border" data-testid="checkout-payment-notice">
              <CreditCard className="h-6 w-6 text-coral shrink-0" />
              <div>
                <p className="font-heading font-semibold text-foreground">Choisissez votre mode de paiement à l'étape suivante</p>
                <p className="text-sm text-muted-foreground">Cliquez sur « Procéder au paiement » pour voir les modes disponibles. Le paiement en ligne arrive sous peu — le règlement se fait à la cueillette ou à la livraison.</p>
              </div>
            </div>
          </Section>
        </div>

        {/* Summary */}
        <aside className="lg:sticky lg:top-24 self-start">
          <div className="rounded-2xl bg-ink-800 border border-border p-6">
            <h3 className="font-heading font-bold text-foreground mb-4">Récapitulatif</h3>
            <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
              {items.map((i) => (
                <div key={i.lineId} className="flex gap-3 text-sm">
                  <img src={i.image} alt="" className="h-12 w-12 rounded-lg object-cover shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-foreground truncate">{i.nom}</p>
                    <p className="text-muted-foreground text-xs">{i.quantity} × {money(i.prix)}{i.guests ? ` · ${i.guests} invités` : ""}</p>
                  </div>
                  <span className="text-muted-foreground">{money(i.prix * i.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="h-px bg-foreground/10 my-4" />
            <div className="space-y-2 text-sm">
              <Row label="Sous-total" value={money(subtotal)} testid="summary-subtotal" />
              <Row label={mode === "livraison" ? "Livraison" : "Cueillette"} value={mode === "livraison" ? money(DELIVERY_FEE) : "Gratuit"} />
            </div>
            <div className="h-px bg-foreground/10 my-4" />
            <div className="flex items-center justify-between mb-5">
              <span className="font-heading font-bold text-foreground">Total</span>
              <span className="font-heading text-2xl font-bold text-coral" data-testid="summary-total">{money(total)}</span>
            </div>
            <button type="submit" disabled={busy} data-testid="checkout-submit"
              className="w-full py-3.5 rounded-xl bg-primary hover:opacity-90 disabled:opacity-60 text-primary-foreground font-heading font-semibold transition-colors active:scale-[0.98] flex items-center justify-center gap-2">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : user ? <Check className="h-5 w-5" /> : <LogIn className="h-5 w-5" />}
              {user ? "Procéder au paiement" : "Se connecter pour payer"}
            </button>
            {!user && <p className="text-xs text-muted-foreground text-center mt-3" data-testid="checkout-guest-note">Un compte est requis pour payer. Vos informations seront préremplies automatiquement.</p>}
          </div>
        </aside>
      </form>

      <PaymentMethodModal
        open={payOpen}
        onClose={() => setPayOpen(false)}
        total={total}
        walletBalance={0}
        onConfirm={placeOrder}
      />
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <div className="rounded-2xl bg-ink-800 border border-border p-6">
      <h2 className="font-heading font-bold text-foreground flex items-center gap-3 mb-4">
        <span className="h-7 w-7 rounded-full bg-primary text-primary-foreground text-sm flex items-center justify-center">{n}</span>
        {title}
      </h2>
      {children}
    </div>
  );
}
function Field({ label, testid, ...props }) {
  return (
    <label className="block">
      <span className="text-sm text-muted-foreground">{label}</span>
      <input {...props} data-testid={testid}
        className="mt-1 w-full px-4 py-2.5 rounded-lg bg-foreground/5 border border-border text-foreground placeholder:text-muted-foreground focus:border-coral focus:outline-none transition-colors" />
    </label>
  );
}
function ModeBtn({ active, onClick, icon: Icon, label, sub, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`p-4 rounded-xl border text-left transition-colors ${active ? "bg-foreground/10 border-coral" : "bg-foreground/5 border-border hover:border-coral/40"}`}>
      <Icon className={`h-6 w-6 mb-2 ${active ? "text-coral" : "text-muted-foreground"}`} />
      <p className="font-heading font-semibold text-foreground text-sm">{label}</p>
      <p className="text-xs text-muted-foreground">{sub}</p>
    </button>
  );
}
function Row({ label, value, testid }) {
  return <div className="flex justify-between"><span className="text-muted-foreground">{label}</span><span className="text-foreground" data-testid={testid}>{value}</span></div>;
}
